/**
 * Colour maths for brand theming. Pure functions, no DOM, no React.
 *
 * ── WHY OKLCH AND NOT HSL ─────────────────────────────────────────────────
 * The palette generator has to answer "the same colour, two steps lighter"
 * for a hue the clinic chose and we have never seen. HSL answers that badly:
 * its L is not perceptual, so `hsl(60 100% 50%)` (yellow) and
 * `hsl(240 100% 50%)` (blue) claim the same lightness while one is nearly
 * white and the other nearly black. A ramp built on HSL is therefore uniform
 * only by accident — it looks right for blues, which is exactly the hue the
 * design system already ships, and falls apart for yellows and greens.
 *
 * OKLab's L IS perceptual, so one set of lightness targets produces an evenly
 * spaced ramp for every hue. That is what lets the same five numbers serve
 * #4a52c4 and #f59e0b.
 *
 * ── AND NOT `oklch()` IN CSS ──────────────────────────────────────────────
 * CSS can do this natively, but the generated values are also written into
 * `<meta name="theme-color">` and compared for equality when deciding whether
 * to repaint. Producing plain `#rrggbb` keeps one representation everywhere.
 */

export interface Rgb {
  /** 0–1, sRGB, gamma-encoded. */
  r: number;
  g: number;
  b: number;
}

export interface Oklch {
  /** Perceptual lightness, 0 (black) – 1 (white). */
  l: number;
  /** Chroma. 0 is grey; sRGB tops out near 0.37 for the most vivid hues. */
  c: number;
  /** Hue angle in degrees, 0–360. */
  h: number;
}

const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

/** True for a `#rrggbb` string, the only form the app stores or accepts. */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_PATTERN.test(value);
}

export function hexToRgb(hex: string): Rgb | null {
  const match = HEX_PATTERN.exec(hex);
  if (!match) return null;
  const n = parseInt(match[1], 16);
  return {
    r: ((n >> 16) & 0xff) / 255,
    g: ((n >> 8) & 0xff) / 255,
    b: (n & 0xff) / 255,
  };
}

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

export function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (n: number) =>
    Math.round(clamp01(n) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** sRGB transfer function, gamma-encoded to linear-light. */
function toLinear(channel: number): number {
  return channel <= 0.04045
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4);
}

/** The inverse: linear-light to gamma-encoded sRGB. */
function toGamma(channel: number): number {
  return channel <= 0.0031308
    ? channel * 12.92
    : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
}

export function rgbToOklch({ r, g, b }: Rgb): Oklch {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  // OKLab matrices, Bjorn Ottosson — https://bottosson.github.io/posts/oklab/
  const l = Math.cbrt(
    0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb,
  );
  const m = Math.cbrt(
    0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb,
  );
  const s = Math.cbrt(
    0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb,
  );

  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const chroma = Math.sqrt(okA * okA + okB * okB);
  // A neutral grey has no meaningful hue angle; atan2(0, 0) is 0, which would
  // silently make every grey "red". Pinning it to 0 is the same number, but
  // stops downstream code from treating it as a hue it can rotate.
  const hue =
    chroma < 1e-6 ? 0 : ((Math.atan2(okB, okA) * 180) / Math.PI + 360) % 360;

  return { l: okL, c: chroma, h: hue };
}

/**
 * OKLCH to sRGB. May return channels outside 0–1 when the requested chroma
 * does not exist in sRGB — `oklchToHex` is what resolves that.
 */
function oklchToRgbUnclamped({ l, c, h }: Oklch): Rgb {
  const hRad = (h * Math.PI) / 180;
  const okA = c * Math.cos(hRad);
  const okB = c * Math.sin(hRad);

  const lCube = (l + 0.3963377774 * okA + 0.2158037573 * okB) ** 3;
  const mCube = (l - 0.1055613458 * okA - 0.0638541728 * okB) ** 3;
  const sCube = (l - 0.0894841775 * okA - 1.291485548 * okB) ** 3;

  return {
    r: toGamma(
      4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube,
    ),
    g: toGamma(
      -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube,
    ),
    b: toGamma(
      -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube,
    ),
  };
}

const IN_GAMUT_EPSILON = 1 / 512;

function isInGamut({ r, g, b }: Rgb): boolean {
  return [r, g, b].every(
    (n) => n >= -IN_GAMUT_EPSILON && n <= 1 + IN_GAMUT_EPSILON,
  );
}

/**
 * OKLCH to `#rrggbb`, reducing chroma until the colour exists in sRGB.
 *
 * Naively clipping the channels is what produces the classic "vivid colour
 * turns muddy or shifts hue when lightened" artefact: clipping moves the
 * colour in whatever direction the overflow happened to point. Walking the
 * chroma down instead keeps the hue and the lightness the generator asked
 * for and gives up only saturation, which is the one of the three the eye
 * forgives.
 *
 * Twenty bisection steps resolve chroma far below the ~0.002 that a different
 * 8-bit channel value requires, so the result is exact in practice.
 */
export function oklchToHex(color: Oklch): string {
  const target: Oklch = {
    l: clamp01(color.l),
    c: Math.max(0, color.c),
    h: ((color.h % 360) + 360) % 360,
  };

  const direct = oklchToRgbUnclamped(target);
  if (isInGamut(direct)) return rgbToHex(direct);

  let low = 0;
  let high = target.c;
  for (let i = 0; i < 20; i += 1) {
    const mid = (low + high) / 2;
    if (isInGamut(oklchToRgbUnclamped({ ...target, c: mid }))) low = mid;
    else high = mid;
  }
  return rgbToHex(oklchToRgbUnclamped({ ...target, c: low }));
}

export function hexToOklch(hex: string): Oklch | null {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToOklch(rgb) : null;
}

/** WCAG 2.1 relative luminance. */
export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return (
    0.2126 * toLinear(rgb.r) +
    0.7152 * toLinear(rgb.g) +
    0.0722 * toLinear(rgb.b)
  );
}

/** WCAG 2.1 contrast ratio, 1–21. Argument order does not matter. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Rounded to one decimal, the way a contrast checker reports it. */
export function formatRatio(ratio: number): string {
  return `${Math.round(ratio * 10) / 10}:1`;
}

/**
 * The best ink for glyphs sitting ON a filled surface.
 *
 * Tries the two inks the design system actually owns — white and the primary
 * text colour — and returns the one with more contrast. It does not invent a
 * third: an "on" colour that is neither of the app's two inks would be a new
 * colour nobody chose, sitting on a surface whose own colour is already a
 * variable.
 */
export function pickInkOn(
  fill: string,
  inks: readonly [string, string],
): { color: string; ratio: number } {
  const first = { color: inks[0], ratio: contrastRatio(inks[0], fill) };
  const second = { color: inks[1], ratio: contrastRatio(inks[1], fill) };
  return first.ratio >= second.ratio ? first : second;
}

/**
 * Walks a colour's lightness until it clears `target` contrast against `bg`,
 * keeping its hue and as much of its chroma as sRGB allows.
 *
 * This is the guarantee that makes an arbitrary brand colour safe to ship: a
 * clinic that picks a pale yellow gets that yellow on its buttons, and a
 * DARKENED yellow — not the yellow — wherever the brand colour renders as
 * text. Without it, "use your own colour" and "meet WCAG AA" are in direct
 * conflict and the product silently picks the first.
 *
 * Direction is inferred from the background so one function serves the light
 * theme (darken toward the page) and the dark one (lighten).
 */
export function ensureContrast(
  hex: string,
  background: string,
  target: number,
): string {
  if (contrastRatio(hex, background) >= target) return hex;

  const start = hexToOklch(hex);
  if (!start) return hex;

  // Move AWAY from the background: on a light page that means darker, on a
  // dark page lighter. Judged by luminance rather than by a theme flag, so it
  // stays correct for a background this function has not been told about.
  const goDarker = relativeLuminance(background) > 0.18;
  const step = goDarker ? -0.01 : 0.01;

  let best = hex;
  let bestRatio = contrastRatio(hex, background);

  for (let i = 1; i <= 100; i += 1) {
    const l = start.l + step * i;
    if (l <= 0 || l >= 1) break;
    const candidate = oklchToHex({ ...start, l });
    const ratio = contrastRatio(candidate, background);
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
    if (ratio >= target) return candidate;
  }

  // Unreachable for any sRGB colour against these backgrounds, but a palette
  // is not the place to throw: return the closest we got rather than a value
  // that is definitely wrong.
  return best;
}
